import React, { useState } from "react";
import {
  IonBadge,
  IonButton,
  IonContent,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonList,
  IonPage,
  IonSpinner
} from "@ionic/react";
import Header from "../components/Header";
import { useUsersStore } from "../stores/users.store";
import { deleteOrder, updateOrder } from "../lib/firebase";
import { checkmarkCircle, trashBin } from "ionicons/icons";

interface ActivityProps {
  id: string;
  allocated: boolean;
  type: string;
  quantity: number;
  description?: string;
  lobbyName?: string;
  lobbyId?: string;
  recipientName?: string;
  senderName?: string;
  botName?: string;
  botId?: string;
  timestamp: string;
  read: boolean;
  user: string;
}

const ActivityItem: React.FC<{
  activity: ActivityProps;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
}> = ({ activity, onRead, onDelete }) => {
  const isCredit = ["winnings", "lobby-payout", "receive-quo", "buy"].includes(
    activity?.type
  );
  const isDebit = ["auto-buy-in", "buy-in", "send-quo"].includes(
    activity?.type
  );
  const quantity = activity?.quantity.toFixed(2);
  const timestamp = new Date(activity?.timestamp).toLocaleString();

  const handleItemClick = () => {
    if (!activity.read) {
      onRead(activity.id);
    }
  };

  const handleDelete = () => {
    onDelete(activity.id);
  };

  return (
    <IonItemSliding>
      <IonItem onClick={handleItemClick} button>
        {!activity.read && (
          <IonBadge className="absolute bottom-1 right-10 bg-primary-60">
            Unread
          </IonBadge>
        )}
        <div className="absolute bottom-0 right-4">
          {!activity?.allocated && <IonSpinner className="w-[12px] h-[12px]" />}
          {activity?.allocated && (
            <IonIcon
              icon={checkmarkCircle}
              className="text-sm text-green-500"
            />
          )}
        </div>
        <div className="flex flex-col grow text-xs text-white">
          <div className="flex flex-row text-sm py-1 justify-between">
            {isCredit && (
              <div className="text-green-400">
                {activity.type.includes("receive-quo")
                  ? "Received"
                  : activity.type === "buy"
                  ? "Bought"
                  : "Payout Received"}
              </div>
            )}
            {isDebit && (
              <div className="text-red-500">
                {activity.type.includes("buy-in") ? "Buy In" : "Sent"}
              </div>
            )}
            <div>
              <span className={isCredit ? "text-green-500" : "text-red-500"}>
                {quantity}
              </span>{" "}
              QUO
            </div>
          </div>
          <div className="font-semibold">
            {activity.type.includes("send-quo")
              ? `To: ${activity?.recipientName}`
              : activity.type.includes("receive-quo")
              ? `From: ${activity?.senderName}`
              : activity.type === "buy"
              ? "Payfast transaction"
              : activity?.description
              ? activity.description
              : `${activity?.botName} - ${activity?.lobbyName}`}
          </div>
          <div className="text-xs text-slate-200 py-1">{timestamp}</div>
        </div>
      </IonItem>
      <IonItemOptions side="end">
        <IonItemOption color="danger" onClick={handleDelete}>
          <IonIcon icon={trashBin} slot="icon-only" />
        </IonItemOption>
      </IonItemOptions>
    </IonItemSliding>
  );
};

const Activity: React.FC = () => {
  const [userActivity, unreadActivity] = useUsersStore(state => [
    state.userActivity,
    state.unreadActivity
  ]);
  const [disableInfiniteScroll, setDisableInfiniteScroll] =
    useState<boolean>(false);
  const [displayCount, setDisplayCount] = useState<number>(20);

  async function searchNext($event: CustomEvent<void>) {
    const newDisplayCount = displayCount + 20;
    setDisplayCount(newDisplayCount);

    if (newDisplayCount >= userActivity.length) {
      setDisableInfiniteScroll(true);
    }

    ($event.target as HTMLIonInfiniteScrollElement).complete();
  }

  const handleRead = async (id: string) => {
    await updateOrder(id, { read: true });
  };

  const handleDelete = async (id: string) => {
    await deleteOrder(id);
  };

  const markAllAsRead = async () => {
    const unreadOrders = userActivity.filter(
      (activity: ActivityProps) => !activity.read
    );
    await Promise.all(
      unreadOrders.map((activity: ActivityProps) =>
        updateOrder(activity.id, { read: true })
      )
    );
  };

  return (
    <IonPage>
      <Header title="Activity" />
      <IonContent fullscreen>
        {userActivity?.length > 0 ? (
          <>
            <div className="flex flex-row justify-end">
              <IonButton
                className="text-xs font-semibold text-primary-20 bg-primary-90"
                onClick={markAllAsRead}
                disabled={unreadActivity === 0}
                fill="clear"
              >
                Mark All As Read
              </IonButton>
            </div>
            <IonList>
              {userActivity
                .sort((a: ActivityProps, b: ActivityProps) =>
                  a.timestamp > b.timestamp ? -1 : 1
                )
                .slice(0, displayCount)
                .map((activity: ActivityProps) => (
                  <ActivityItem
                    key={activity.id}
                    activity={activity}
                    onRead={handleRead}
                    onDelete={handleDelete}
                  />
                ))}
              <IonInfiniteScroll
                threshold="100px"
                disabled={disableInfiniteScroll}
                onIonInfinite={searchNext}
                className="bg-primary-90 text-white"
              >
                <IonInfiniteScrollContent loadingText="Loading more activity..."></IonInfiniteScrollContent>
              </IonInfiniteScroll>
            </IonList>
          </>
        ) : (
          <div>No activity yet</div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Activity;
